import { makeVar, ReactiveVar } from "@apollo/client";

class LocalStorageReactiveVar<T> {
  private reactiveVar: ReactiveVar<T>;
  private key: string;
  private serialize: (value: T) => string;
  private deserialize: (value: string) => T;

  constructor(options: { key: string; defaultValue: T }) {
    this.key = options.key;
    this.serialize = JSON.stringify;
    this.deserialize = JSON.parse;

    const storedValue = this.loadFromStorage();
    if (storedValue !== null) {
      this.reactiveVar = makeVar<T>(storedValue);
    } else {
      this.reactiveVar = makeVar<T>(options.defaultValue);
      this.saveToStorage(options.defaultValue);
    }
  }

  private loadFromStorage(): T | null {
    try {
      const item = localStorage.getItem(this.key);
      return item ? this.deserialize(item) : null;
    } catch (error) {
      console.warn(`Failed to load ${this.key} from localStorage:`, error);
      return null;
    }
  }

  private saveToStorage(value: T): void {
    try {
      localStorage.setItem(this.key, this.serialize(value));
    } catch (error) {
      console.warn(`Failed to save ${this.key} to localStorage:`, error);
    }
  }

  get(): T {
    return this.reactiveVar();
  }

  set(value: T): void {
    this.reactiveVar(value);
    this.saveToStorage(value);
  }

  getReactiveVar(): ReactiveVar<T> {
    return this.reactiveVar;
  }

  clear(defaultValue: T): void {
    localStorage.removeItem(this.key);
    this.reactiveVar(defaultValue);
  }
}

export function makeLocalStorageVar<T>(
  key: string,
  defaultValue: T,
): LocalStorageReactiveVar<T> {
  return new LocalStorageReactiveVar({ key, defaultValue });
}

export { LocalStorageReactiveVar };
